import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Camera, UploadSimple, Sparkle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PillScanner() {
  const fileInput = useRef(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image (JPEG, PNG, WEBP).");
      return;
    }
    setPreview(URL.createObjectURL(file));
    setResult(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pill-scanner/identify", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed. Try again with a clearer photo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#576A62] font-semibold">Identify a pill</div>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold">AI pill scanner</h1>
        <p className="mt-2 text-[#576A62] max-w-2xl">
          Snap or upload a clear photo of a single pill on a plain surface. We'll analyze color, shape,
          and any imprint. <strong className="text-[#B05A43]">This is a visual aid only</strong> —
          always confirm with your pharmacist before taking anything.
        </p>
        <div className="mt-3 inline-flex items-center gap-2 text-xs rounded-full bg-[#3E7B5C]/12 text-[#3E7B5C] font-medium px-3 py-1.5" data-testid="pill-privacy-note">
          <Sparkle size={14} weight="duotone" /> Your photo is analyzed once and then permanently discarded. No image is saved.
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#EBE8E0] rounded-2xl p-6">
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => onFile(e.target.files?.[0])}
            className="hidden"
            data-testid="pill-scanner-input"
          />
          {preview ? (
            <img src={preview} alt="Pill preview" className="w-full aspect-square object-contain rounded-xl bg-[#EBE8E0]/40" data-testid="pill-scanner-preview" />
          ) : (
            <div
              onClick={() => fileInput.current?.click()}
              className="w-full aspect-square rounded-xl border-2 border-dashed border-[#EBE8E0] flex flex-col items-center justify-center bg-[#FDFBF7] cursor-pointer hover:bg-[#EBE8E0]/30 transition-colors"
              data-testid="pill-scanner-dropzone"
            >
              <Camera size={72} weight="duotone" className="text-[#2C5545]" />
              <div className="mt-4 font-heading text-xl font-bold">Tap to take a photo</div>
              <div className="text-sm text-[#576A62] mt-1">or upload from your device</div>
            </div>
          )}
          <div className="mt-4 flex gap-3">
            <button
              data-testid="pill-scanner-upload-btn"
              onClick={() => fileInput.current?.click()}
              className="flex-1 rounded-full bg-[#2C5545] hover:bg-[#224235] text-white font-semibold py-3 inline-flex items-center justify-center gap-2"
            >
              <UploadSimple weight="bold" size={18}/> {preview ? "Choose another" : "Choose photo"}
            </button>
            {preview && (
              <button
                data-testid="pill-scanner-clear-btn"
                onClick={() => { setPreview(null); setResult(null); }}
                className="rounded-full border border-[#EBE8E0] hover:bg-[#EBE8E0]/60 px-5 py-3"
              >Clear</button>
            )}
          </div>
        </div>

        <div className="bg-white border border-[#EBE8E0] rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle size={22} weight="duotone" className="text-[#2C5545]" />
            <h3 className="font-heading text-xl font-bold">Identification</h3>
          </div>
          {loading ? (
            <div className="space-y-3" data-testid="pill-scanner-loading">
              <div className="h-6 bg-[#EBE8E0]/60 rounded animate-pulse" />
              <div className="h-6 bg-[#EBE8E0]/60 rounded w-2/3 animate-pulse" />
              <div className="h-24 bg-[#EBE8E0]/60 rounded animate-pulse" />
              <div className="text-sm text-[#576A62] mt-2">Analyzing photo…</div>
            </div>
          ) : result ? (
            <div className="space-y-4" data-testid="pill-scanner-result">
              <div className="grid grid-cols-3 gap-3">
                <Stat k="Color" v={result.color} />
                <Stat k="Shape" v={result.shape} />
                <Stat k="Imprint" v={result.imprint} />
              </div>
              {result.description && <p className="text-[#576A62] leading-relaxed">{result.description}</p>}
              {Array.isArray(result.candidates) && result.candidates.length > 0 && (
                <div>
                  <div className="text-sm font-semibold mb-2">Likely candidates</div>
                  <div className="space-y-2">
                    {result.candidates.map((c, i) => (
                      <div key={i} className="border border-[#EBE8E0] rounded-xl p-4" data-testid={`candidate-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold">{c.name || "Unknown"}</div>
                          <span className={`text-xs px-3 py-1 rounded-full ${
                            c.confidence === "high" ? "bg-[#3E7B5C]/15 text-[#3E7B5C]" :
                            c.confidence === "medium" ? "bg-[#D49A4C]/15 text-[#B37927]" :
                            "bg-[#EBE8E0] text-[#576A62]"
                          }`}>{c.confidence || "low"}</span>
                        </div>
                        {c.dosage && <div className="text-sm text-[#576A62] mt-1">{c.dosage}</div>}
                        {c.notes && <div className="text-sm text-[#576A62] mt-1">{c.notes}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-start gap-2 rounded-xl bg-[#C86A51]/8 border border-[#C86A51]/30 p-4 text-sm text-[#B05A43]">
                <Warning size={20} weight="duotone" className="shrink-0 mt-0.5" />
                <div>{result.disclaimer}</div>
              </div>
            </div>
          ) : (
            <div className="text-[#576A62]">
              <img src="https://images.unsplash.com/photo-1628771065518-0d82f1938462" alt="" className="rounded-xl w-full h-40 object-cover" />
              <div className="mt-4">Once you upload a clear photo of a pill, we'll try to identify it here. Best results come from a single pill on a plain, well-lit surface.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="rounded-xl bg-[#FDFBF7] border border-[#EBE8E0] p-3">
      <div className="text-xs uppercase tracking-widest text-[#576A62]">{k}</div>
      <div className="mt-1 font-semibold text-[#1A2520]">{v || "—"}</div>
    </div>
  );
}
